/**
 * TerrainForge — 하이트맵 지형 작업대.
 *
 * 왼쪽: 하이트맵·층 텍스처 드롭, 크기/높이/수면/스플랫 문턱 슬라이더, glb 저장.
 * 무대: 구운 알베도를 입은 지형 + 수면 + (있으면) 주인공이 지형 중앙 땅 위에 선다.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { LAYER_LABEL, terrainRef, useTerrainStore, type LayerKey } from '../state/terrain.ts';
import { forgedMeshRef } from '../state/view.ts';
import { heightAt, type TerrainParams } from '../core/terrain/heightmap.ts';
import { KIT_GALLERY } from '../core/map/kitGallery.ts';
import { downloadBlob, exportSceneGLB } from '../core/export/glb.ts';
import { useStudioEnvironment } from './environment.ts';

function TerrainMesh() {
  const version = useTerrainStore((s) => s.version);
  const rebuild = useTerrainStore((s) => s.rebuild);
  useEffect(() => { if (!terrainRef.current) rebuild(); }, [rebuild]);
  const built = terrainRef.current;
  void version;
  if (!built) return null;
  return <primitive object={built.mesh} />;
}

function Water() {
  const p = useTerrainStore((s) => s.params);
  if (p.waterLevel <= 0) return null;
  return (
    <mesh name="water" rotation={[-Math.PI / 2, 0, 0]} position={[0, p.waterLevel * p.heightScale, 0]}>
      <planeGeometry args={[p.size * 3, p.size * 3]} />
      <meshStandardMaterial color={0x2b6ea8} transparent opacity={0.72} roughness={0.15} metalness={0.05} />
    </mesh>
  );
}

function Hero() {
  const version = useTerrainStore((s) => s.version);
  const p = useTerrainStore((s) => s.params);
  const show = useTerrainStore((s) => s.showHero);
  const src = forgedMeshRef.current;
  const mixer = useRef<THREE.AnimationMixer | null>(null);
  const obj = useMemo(() => {
    if (!src) return null;
    const c = skeletonClone(src) as THREE.SkinnedMesh;
    c.geometry.computeBoundingBox();
    const bb = c.geometry.boundingBox!;
    const s = 1.8 / Math.max(1e-6, bb.max.y - bb.min.y);
    c.scale.setScalar(s);
    c.position.set(-(bb.min.x + bb.max.x) / 2 * s, -bb.min.y * s, -(bb.min.z + bb.max.z) / 2 * s);
    c.frustumCulled = false;
    const g = new THREE.Group(); g.add(c);
    mixer.current = new THREE.AnimationMixer(c);
    const idle = src.animations.find((a) => a.name === 'idle');
    if (idle) mixer.current.clipAction(idle).play();
    return g;
  }, [src]);
  useFrame((_, d) => mixer.current?.update(Math.min(d, 0.1)));
  const y = useMemo(() => { void version; const f = terrainRef.current?.field; return f ? heightAt(f, p.size, p.heightScale, 0, 0) : 0; }, [version, p.size, p.heightScale]);
  if (!obj || !show) return null;
  return <group position={[0, y, 0]}><primitive object={obj} /></group>;
}

function Lights() {
  const environment = useStudioEnvironment();
  const scene = useThree((s) => s.scene);
  const gl = useThree((s) => s.gl);
  const size = useTerrainStore((s) => s.params.size);
  useEffect(() => {
    scene.environment = environment;
    scene.background = new THREE.Color(0x0a0c12);
    scene.fog = new THREE.Fog(0x0a0c12, size * 1.2, size * 3.2);
    gl.toneMapping = THREE.NeutralToneMapping; gl.toneMappingExposure = 1.0;
    return () => { scene.fog = null; };
  }, [scene, environment, gl, size]);
  return (
    <>
      <directionalLight position={[30, 40, 20]} intensity={1.3} castShadow shadow-mapSize={[2048, 2048]} shadow-camera-left={-40} shadow-camera-right={40} shadow-camera-top={40} shadow-camera-bottom={-40} shadow-camera-far={150} />
      <ambientLight intensity={0.3} />
    </>
  );
}

export function TerrainStage() {
  const busy = useTerrainStore((s) => s.busy);
  const size = useTerrainStore((s) => s.params.size);
  return (
    <div className="stage">
      <Canvas shadows dpr={[1, 1.5]} camera={{ position: [size * 0.55, size * 0.45, size * 0.75], fov: 45, near: 0.1, far: 800 }} gl={{ antialias: true, preserveDrawingBuffer: true }}>
        <Lights />
        <OrbitControls target={[0, 2, 0]} makeDefault maxPolarAngle={Math.PI * 0.49} />
        <TerrainMesh />
        <Water />
        <Hero />
      </Canvas>
      {busy && <div className="overlay"><div className="spinner" /><div className="msg">{busy}</div></div>}
      <div className="stage-tip">드래그 회전 · 휠 줌 · 우클릭 이동</div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Slider({ k, label, min, max, step, fmt }: { k: keyof TerrainParams; label: string; min: number; max: number; step: number; fmt?: (v: number) => string }) {
  const v = useTerrainStore((s) => s.params[k]);
  const setParam = useTerrainStore((s) => s.setParam);
  return (
    <label className="field">
      <span className="field-head"><span className="k">{label}</span><span className="v">{fmt ? fmt(v) : v}</span></span>
      <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => setParam(k, Number(e.target.value))} />
    </label>
  );
}

function Inputs() {
  const heightmapUrl = useTerrainStore((s) => s.heightmapUrl);
  const heightmapName = useTerrainStore((s) => s.heightmapName);
  const setHeightmap = useTerrainStore((s) => s.setHeightmap);
  const reseed = useTerrainStore((s) => s.reseed);
  const layerUrls = useTerrainStore((s) => s.layerUrls);
  const setLayer = useTerrainStore((s) => s.setLayer);
  const setLayerUrl = useTerrainStore((s) => s.setLayerUrl);
  const hm = useRef<HTMLInputElement>(null);
  const li = useRef<Record<string, HTMLInputElement | null>>({});
  const bundled = (k: LayerKey) => KIT_GALLERY.find((g) => g.id === `tex-${k}`);
  const bundledHm = KIT_GALLERY.find((g) => g.id === 'heightmap');
  const bundledLoad = async (url: string) => { const b = await (await fetch(url)).blob(); await setHeightmap(new File([b], 'heightmap.webp', { type: 'image/webp' })); };

  return (
    <div className="section">
      <h2>하이트맵</h2>
      <div className="thumb-row">
        {heightmapUrl ? <img className="thumb" src={heightmapUrl} alt="" /> : <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 10, color: 'var(--muted)' }}>fBm</div>}
        <div className="filemeta">
          <div className="name">{heightmapName || '절차적 노이즈 섬'}</div>
          <div className="sub">밝을수록 높음 · 회색 이미지</div>
        </div>
      </div>
      <div className="chips" style={{ marginTop: 8 }}>
        <button className="chip ghost" onClick={() => hm.current?.click()}>이미지 열기</button>
        {bundledHm && <button className="chip ghost" onClick={() => void bundledLoad(bundledHm.url)}>번들 하이트맵</button>}
        <button className="chip ghost" onClick={() => { void setHeightmap(null); reseed(); }}>노이즈 새로</button>
        <input ref={hm} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void setHeightmap(f); e.target.value = ''; }} />
      </div>
      <h2 style={{ marginTop: 14 }}>스플랫 층</h2>
      <div className="layers">
        {(['grass', 'dirt', 'rock', 'snow'] as LayerKey[]).map((k) => (
          <div key={k} className="layer">
            <button className="tile" onClick={() => li.current[k]?.click()} title={`${LAYER_LABEL[k]} 텍스처 열기`}>
              {layerUrls[k] ? <img src={layerUrls[k]} alt="" /> : <div className="ph" data-k={k} />}
              <span>{LAYER_LABEL[k]}</span>
            </button>
            {bundled(k) && !layerUrls[k] && <button className="chip ghost small" onClick={() => void setLayerUrl(k, bundled(k)!.url, k)}>번들</button>}
            {layerUrls[k] && <button className="chip ghost small" onClick={() => void setLayer(k, null)}>제거</button>}
            <input ref={(el) => { li.current[k] = el; }} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void setLayer(k, f); e.target.value = ''; }} />
          </div>
        ))}
      </div>
      <p className="hint">층이 없으면 단색. 높이·경사로 네 층을 섞어 <b>알베도 한 장</b>으로 굽습니다 — 뷰어와 glb 가 같은 그림입니다.</p>
    </div>
  );
}

function Params() {
  return (
    <div className="section">
      <h2>지형</h2>
      <Slider k="size" label="한 변 길이" min={16} max={200} step={4} fmt={(v) => `${v}m`} />
      <Slider k="resolution" label="격자 정점" min={33} max={257} step={32} fmt={(v) => `${v}²`} />
      <Slider k="heightScale" label="최대 높이" min={1} max={40} step={0.5} fmt={(v) => `${v}m`} />
      <Slider k="smooth" label="스무딩" min={0} max={4} step={0.1} />
      <Slider k="island" label="섬 (가장자리 낮춤)" min={0} max={1} step={0.05} />
      <Slider k="waterLevel" label="수면" min={0} max={0.6} step={0.01} />
      <h2 style={{ marginTop: 10 }}>스플랫</h2>
      <Slider k="sandHeight" label="흙·모래 높이 이하" min={0} max={0.8} step={0.01} />
      <Slider k="rockSlope" label="바위 경사 이상" min={0.1} max={0.95} step={0.01} />
      <Slider k="snowHeight" label="눈 높이 이상" min={0.3} max={1} step={0.01} />
      <Slider k="tiling" label="텍스처 반복" min={1} max={40} step={1} fmt={(v) => `${v}×`} />
    </div>
  );
}

function Export() {
  const showHero = useTerrainStore((s) => s.showHero);
  const wireframe = useTerrainStore((s) => s.wireframe);
  const toggle = useTerrainStore((s) => s.toggle);
  const p = useTerrainStore((s) => s.params);
  const version = useTerrainStore((s) => s.version);
  const [last, setLast] = useState('');
  const [busy, setBusy] = useState(false);
  const hasHero = Boolean(forgedMeshRef.current);
  const built = terrainRef.current;
  void version;

  const save = async () => {
    const t = terrainRef.current; if (!t) return;
    setBusy(true);
    try {
      const root = new THREE.Group(); root.name = 'terrain';
      root.add(t.mesh.clone());
      if (p.waterLevel > 0) {
        const w = new THREE.Mesh(new THREE.PlaneGeometry(p.size, p.size), new THREE.MeshStandardMaterial({ color: 0x2b6ea8, transparent: true, opacity: 0.72, roughness: 0.15 }));
        w.name = 'water'; w.rotation.x = -Math.PI / 2; w.position.y = p.waterLevel * p.heightScale; root.add(w);
      }
      root.updateMatrixWorld(true);
      const blob = await exportSceneGLB(root);
      downloadBlob(blob, 'terrain.glb');
      setLast(`${(blob.size / 1024 / 1024).toFixed(2)}MB · 삼각형 ${(t.geometry.index?.count ?? 0) / 3 | 0}`);
    } catch (err) { setLast(`실패: ${err instanceof Error ? err.message : String(err)}`); }
    finally { setBusy(false); }
  };
  const saveHeight = () => {
    const t = terrainRef.current; if (!t) return;
    const f = t.field; const c = document.createElement('canvas'); c.width = f.width; c.height = f.height;
    const id = new ImageData(f.width, f.height);
    for (let i = 0; i < f.width * f.height; i++) { const v = f.data[i]! * 255; id.data[i * 4] = v; id.data[i * 4 + 1] = v; id.data[i * 4 + 2] = v; id.data[i * 4 + 3] = 255; }
    c.getContext('2d')!.putImageData(id, 0, 0);
    c.toBlob((b) => { if (b) downloadBlob(b, 'heightmap.png'); }, 'image/png');
  };
  const saveAlbedo = () => { const t = terrainRef.current; if (!t) return; (t.albedo.image as HTMLCanvasElement).toBlob((b) => { if (b) downloadBlob(b, 'terrain-albedo.png'); }, 'image/png'); };

  return (
    <div className="section">
      <h2>내보내기</h2>
      <div className="chips" style={{ marginBottom: 8 }}>
        <button className="chip" aria-pressed={showHero} disabled={!hasHero} onClick={() => toggle('showHero')} title={hasHero ? '' : '캐릭터 작업대에서 먼저 캐릭터를 만드세요'}>주인공 표시</button>
        <button className="chip" aria-pressed={wireframe} onClick={() => toggle('wireframe')}>와이어</button>
      </div>
      <div className="btn-row">
        <button className="btn primary" disabled={!built || busy} onClick={() => void save()}>{busy ? '굽는 중…' : '지형 .glb 저장'}</button>
        <button className="btn" disabled={!built} onClick={saveHeight}>하이트맵 .png</button>
        <button className="btn" disabled={!built} onClick={saveAlbedo}>알베도 .png</button>
      </div>
      {last && <p className="hint">{last}</p>}
      <p className="hint">Unity 의 Terrain 은 하이트맵 .png(RAW 대신) 로, 다른 엔진은 glb 메시로 가져가면 됩니다.</p>
    </div>
  );
}

export function TerrainPanel() {
  const error = useTerrainStore((s) => s.error);
  return (
    <aside className="panel left">
      <Inputs />
      {error && <div className="error-box">{error}</div>}
      <Params />
      <Export />
    </aside>
  );
}
